import { create } from "zustand";
import { persist } from "zustand/middleware";
import { safeLocalStorage } from "../utils/safeStorage";
import { formatPrice, getProductUnitDisplay } from "../utils/mapWooProduct";
import { getDisplayCategory } from "../utils/productDisplay";

const CART_STORAGE_KEY = "caf-sports-cart";

/** @typedef {{ id: number, name: string, price: number, formattedPrice?: string, image: string, quantity: number, slug?: string, sku?: string, unit?: string, unitWeight?: string, unitLabel?: string, categoryName?: string }} CartItem */

function parsePrice(price) {
  const amount = Number.parseFloat(price);
  return Number.isNaN(amount) ? 0 : amount;
}

export function productToCartItem(product, quantity = 1) {
  const category = getDisplayCategory(product.categories);

  return {
    id: product.id,
    name: product.name,
    price: parsePrice(product.price),
    formattedPrice: product.formattedPrice || formatPrice(String(product.price)),
    image: product.image || "",
    quantity: Math.max(1, quantity),
    slug: product.slug,
    sku: product.sku || "",
    unit: product.unit || "",
    unitWeight: product.unitWeight || "",
    unitLabel: product.unitLabel || getProductUnitDisplay(product),
    categoryName: category?.name || "",
  };
}

function calculateSubtotal(items) {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],

      addItem: (product, quantity = 1) => {
        const qty = Math.max(1, quantity);
        set((state) => {
          const existing = state.items.find((item) => item.id === product.id);
          if (existing) {
            return {
              items: state.items.map((item) =>
                item.id === product.id
                  ? {
                      ...productToCartItem(product, item.quantity + qty),
                      quantity: item.quantity + qty,
                    }
                  : item,
              ),
            };
          }
          return {
            items: [...state.items, productToCartItem(product, qty)],
          };
        });
      },

      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }));
      },

      updateQuantity: (id, quantity) => {
        const qty = Math.max(1, quantity);
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, quantity: qty } : item,
          ),
        }));
      },

      clearCart: () => set({ items: [] }),

      getItemCount: () =>
        get().items.reduce((sum, item) => sum + item.quantity, 0),

      getSubtotal: () => calculateSubtotal(get().items),

      getTotal: () => calculateSubtotal(get().items),
    }),
    { name: CART_STORAGE_KEY, storage: safeLocalStorage },
  ),
);

export function selectCartCount(state) {
  return state.items.reduce((sum, item) => sum + item.quantity, 0);
}
